/**
 * REST auth client (core-architecture §4, security §2.1). Mirrors AuthEndpoints.cs:
 *   POST /api/v1/auth/guest    { displayName? } -> AuthResponse
 *   POST /api/v1/auth/refresh  { refresh }      -> AuthResponse (401 AUTH_* on failure)
 *
 * Both endpoints are anonymous (they mint/rotate credentials), so `auth: false`.
 */
import type { HttpClient } from '../rest/http.ts';

/**
 * The credential bundle from guest signup / refresh.
 *
 * The refresh token rides an <c>HttpOnly; Secure; SameSite=Strict</c> cookie the backend sets (security
 * §2.1) — JavaScript can never read it. The backend still echoes `refresh` in the body for console clients
 * (OS secure store); the web client ignores it and never stores it, hence the field is optional here.
 */
export interface AuthResponse {
  access: string;
  /** Present in the body for console clients; the web relies on the HttpOnly cookie and ignores this. */
  refresh?: string;
  playerId: string;
  authLevel: string;
  /** Seconds until the access token expires. */
  expiresIn: number;
}

export class AuthClient {
  constructor(private readonly http: HttpClient) {}

  guest(displayName?: string): Promise<AuthResponse> {
    // `credentials: include` lets the backend's Set-Cookie (the refresh cookie) be stored by the browser.
    return this.http.post<AuthResponse>('/api/v1/auth/guest', {
      auth: false,
      credentials: 'include',
      body: { displayName: displayName ?? null },
    });
  }

  /** Rotates the access token using the HttpOnly refresh cookie — no token in the body (security §2.1). */
  refresh(): Promise<AuthResponse> {
    return this.http.post<AuthResponse>('/api/v1/auth/refresh', {
      auth: false,
      credentials: 'include',
    });
  }
}
